import { styled, Typography, useThemeProps, type SxProps } from "@mui/material";
import { RecordContextProvider, ReferenceFieldContextProvider, ResourceContextProvider, useFieldValue, useGetRecordRepresentation, useRecordContext, useReferenceFieldContext, useTranslate, type Identifier, type RaRecord, type UseReferenceFieldControllerResult } from "ra-core";
import { Link, ReferenceFieldClasses } from "ra-ui-materialui";
import { memo, type ReactNode } from "react";

const ReferenceFieldBase = <
    ReferenceRecordType extends RaRecord = RaRecord,
    RecordType extends RaRecord<Identifier> = RaRecord<Identifier>,
>(
    props: ReferenceFieldBaseProps<ReferenceRecordType, RecordType>
) => {
    const { children, reference, render, map = (x) => x } = props;

    if (!render && !children) {
        throw new Error(
            "<ReferenceFieldBase> requires either a 'render' prop or 'children' prop"
        );
    }
    const record = useRecordContext<ReferenceRecordType>();
    const referenceRecord: RecordType = record ? map(record) : undefined as any;
    const id = useFieldValue({ ...props, record: referenceRecord as any });
    const controllerProps: UseReferenceFieldControllerResult<RecordType> = { 
        isFetching: false, 
        isLoading: false, 
        isPending: false,
        isPaused: false,
        isPlaceholderData: false,
        link: `/${reference}/${id}`,
        refetch: async () => ({ data: referenceRecord } as any), 
        referenceRecord: referenceRecord,
    }


    return (
        <ResourceContextProvider value={props.reference}>
            <ReferenceFieldContextProvider value={controllerProps}>
                <RecordContextProvider value={referenceRecord}>
                    {render ? render(referenceRecord) : children}
                </RecordContextProvider>
            </ReferenceFieldContextProvider>
        </ResourceContextProvider>
    );
};

export interface ReferenceFieldBaseProps<
    ReferenceRecordType extends RaRecord<Identifier> = RaRecord<Identifier>,
    RecordType extends RaRecord<Identifier> = RaRecord<Identifier>,
> {
    map?: (x: ReferenceRecordType) => RecordType;
    render?: (props: RecordType) => ReactNode;
    children?: ReactNode;
    className?: string;
    empty?: ReactNode;
    reference: string;
    record?: RecordType;
    source: string;
    sx?: SxProps;
    label?: string;
}

const PREFIX = "RaReferenceField";
const stopPropagation = (e: any) => e.stopPropagation();

const ReferenceFieldView = <
    RecordType extends RaRecord<Identifier> = RaRecord<Identifier>,
    ReferenceRecordType extends RaRecord = RaRecord,
>(
    props: ReferenceFieldBaseProps<RecordType, ReferenceRecordType>
) => {
    const { children, render, className, reference, sx, ...rest } =
        useThemeProps({
            props: props,
            name: PREFIX,
        });
    const referenceFieldContext = useReferenceFieldContext();
    const { link, referenceRecord } = referenceFieldContext;

    const getRecordRepresentation = useGetRecordRepresentation(reference);



    const child = (render ? render(referenceRecord as any) : children) || (
        <Typography component="span" variant="body2">
            {getRecordRepresentation(referenceRecord)}
        </Typography>
    );

    if (link) {
        return (
            <Root
                className={ReferenceFieldClasses.root}
                sx={sx}
                {...rest}
            >
                <Link
                    to={link}
                    // className={ReferenceFieldClasses.link}
                    onClick={stopPropagation}
                    state={{ _scrollToTop: true }}
                    underline="none"
                >
                    {child}
                </Link>
            </Root>
        );
    }

    return (
        <Root
            className={ReferenceFieldClasses.root}
            sx={sx}
            {...rest}
        >
            {child}
        </Root>
    );
};

const PureReferenceFieldView = memo(ReferenceFieldView) as typeof ReferenceFieldView;

const Root = styled("span", {
    name: PREFIX,
    overridesResolver: (_, styles) => ({
        ["&"]: styles.root,
        [`& .${ReferenceFieldClasses.link}`]: styles.link,
    }),
})(({ theme }) => ({
    lineHeight: "initial",
    [`& .${ReferenceFieldClasses.link}`]: {
        "& > *": {
            color: (theme.vars || theme).palette.primary.main,
        },
    },
}));

export const ReferenceField = <
    RecordType extends RaRecord<Identifier> = RaRecord<Identifier>,
    ReferenceRecordType extends RaRecord = RaRecord,
>(
    inProps: ReferenceFieldBaseProps<RecordType, ReferenceRecordType>
) => {
    const props = useThemeProps({
        props: inProps,
        name: PREFIX,
    });
    const {
        children,
        render,
        empty,
        ...rest
    } = props;
    const translate = useTranslate();

    return (
        <ReferenceFieldBase<ReferenceRecordType>
            {...rest as any}
            empty={
                typeof empty === 'string' ? (
                    <Typography component="span" variant="body2">
                        {empty && translate(empty, { _: empty })}
                    </Typography>
                ) : (
                    empty ?? null
                )
            }
        >
            <PureReferenceFieldView<RecordType, ReferenceRecordType>
                {...rest}
                render={render as any}
            >
                {children}
            </PureReferenceFieldView>
        </ReferenceFieldBase>
    );
};